<template>
    <ModalLayout
        :visible="visible"
        @destroy="close"
        @success="submitForm"
        :successDisabled="successDisabled"
        :confirmation-modal="true"
        :confirmation-title="this.$t('digital.order_shipment.confirmation_title')"
        :confirmation-message="this.$t('digital.order_shipment.confirmation_message')"
        :success-label="$t('digital.delivery_form.submit')"
        :success-loading="loading"
        :tabs="tabItems"
        :tabActive="activeTab"
        @tabSelected="(tab) => activeTab = tab"
    >
        <template v-slot:title>
            {{ $t('digital.order_shipment.title') }}
        </template>

        <template v-slot:settingArea>
            <p>{{ $t('digital.order_shipment.description') }}</p>

            <g-form :form="form" class="form-container" ref="formRef" :action="formEndpoint" :before="before"
                    @loading-state="setLoadingState" @fail="setActiveStepForErrors" @success="formSuccess">

                <accordeon-item
                    :title="$t('digital.order_shipment.order_settings')"
                    :top-title="$t('accordeon.step', [1])"
                    :active="activeStep === 1"
                    @select="selectedStep(1)"
                >
                    <div class="is-flex is-flex-direction-column is-justify-content-space-between form-min-height">
                        <div>
                            <label class="group-label">
                                {{ $t('digital.order_shipment.order') }}
                            </label>
                            <g-input name="order_reference" :label="$t('digital.order_shipment.order')" disabled readonly/>

                            <label class="group-label">
                                {{ $t('digital.order_shipment.bundle') }}
                            </label>
                            <g-select :label="$t('digital.order_shipment.ship')" :disabled="shipmentBundleOptions.length === 1"
                                      :options="shipmentBundleOptions" name="shipment_bundle" valuekey="id"/>

                            <label class="group-label">
                                {{ $t('digital.order_shipment.wrapping') }}
                            </label>
                            <g-select has-images :label="$t('digital.order_shipment.wrapping_short')"
                                      :options="wrappingOptions" name="wrapping" valuekey="id"/>
                        </div>

                        <div>
                            <g-button @click.prevent="activeStep++" :is-rounded="false" class="is-pulled-right">
                                {{ $t('digital.create.next_step') }}
                            </g-button>
                        </div>
                    </div>

                </accordeon-item>

                <accordeon-item
                    :title="$t('digital.order_shipment.ship')"
                    :top-title="$t('accordeon.step', [2])"
                    :active="activeStep === 2"
                    @select="selectedStep(2)"
                >
                    <div class="is-flex is-flex-direction-column is-justify-content-space-between form-min-height">
                        <div>
                            <label class="group-label">
                                {{ $t('digital.order_shipment.shipment_method') }}
                            </label>
                            <g-select
                                :label="$t('digital.order_shipment.shipment_method')"
                                :options="availableShipmentMethods"
                                name="shipment_method"
                                valuekey="id"
                            />

                            <div v-if="shouldShowTrackAndTraceWarning && form.shipment_method === 'track-n-trace'"
                                 class="tnt-notification has-text-danger m-0 p-0">
                                <span>
                                    <i class="icon">warning</i>
                                    {{ $t('digital.order_shipment.track_and_trace_unavailable', {
                                        countries: trackAndTraceUnavailableCountries.map(country => country.name).join(', ')
                                    }) }}
                                </span>
                            </div>
                        </div>
                    </div>

                </accordeon-item>

            </g-form>
        </template>

        <template v-slot:previewArea>
            <tabbed-view @tabsInitialized="(tabs) => tabItems = tabs" ref="tabbedView">
                <tabbed-view-item
                    :title="$t('digital.order_shipment.order')"
                    :active="activeTab === 0"
                    blueprint
                >
                    <div>&nbsp;</div>
                    <line-table :title="$t('digital.create.order_total')">
                        <template v-slot:body>
                            <tr v-if="form.wrapping !== 'none'">
                                <td class="minimum-width">{{ order.quantity }}x</td>
                                <td>{{ $t('digital.order_shipment.wrapping_short') }} {{ selectedWrapping['name'] }}</td>
                                <td class="minimum-width">{{ (selectedWrapping['price'] * order.quantity) / 100 | currency }}</td>
                            </tr>

                            <tr v-for="(orderCountry, index) in differentOrderCountries" :key="index">
                                <td class="minimum-width">{{ getShipmentQuantityDisplay(orderCountry) }}</td>
                                <td>
                                    {{ $t(`digital.order_shipment.price_${form.shipment_method}`, { country: orderCountry.name, carrier: orderCountry.carrier }) }}
                                </td>
                                <td class="minimum-width align-right">{{ getShipmentPriceDisplay(orderCountry) | currency }}</td>
                            </tr>
                        </template>

                        <template v-slot:footer>
                            <tr>
                                <td colspan="2" class="has-text-right">{{ $t('digital.create.total_excl_vat') }}</td>
                                <td class="minimum-width">
                                    {{ calculatedShipmentPrice }}
                                </td>
                            </tr>
                        </template>
                    </line-table>
                </tabbed-view-item>

                <tabbed-view-item
                    :title="$t('digital.order_shipment.title_gift_card')"
                    :active="activeTab === 1"
                    blueprint
                >
                    <div class="preview-container">
                        <giftcard-preview
                            :title="previewData.title"
                            :value="previewData.value"
                            :card-side="previewCardSide"
                            :message="previewData.message"
                            hide-title
                            hide-switch-buttons/>
                        <div class="controls">
                            <a href="#" @click.prevent="previewCardSide = 'front'" :class="{'is-active': previewCardSide === 'front'}">
                                {{ $t('digital.create.front') }}
                            </a>

                            <a href="#" @click.prevent="previewCardSide = 'back'" :class="{'is-active': previewCardSide === 'back'}">
                                {{ $t('digital.create.back') }}
                            </a>
                        </div>
                    </div>
                </tabbed-view-item>

                <tabbed-view-item
                    :title="$t('digital.order_shipment.title_addresses')"
                    :active="activeTab === 2"
                    blueprint
                >
                    <template v-slot:header>
                        <strong>{{ $t('digital.order_shipment.addresses') }}</strong>
                    </template>

                    <template v-slot:postheader>
                        <g-form :form="form" :action="formEndpoint" v-if="form !== null" ref="addresses">

                            <ul class="addresses">
                                <template v-for="(address, index) in formAddresses">
                                    <li @click.prevent="toggleAddressPopout(index)" v-if="formAddresses.length > 1">
                                        <div class="address-header" :class="{ 'active': tabStates[index] }">
                                            <div class="name">
                                                <span class="step">
                                                    {{ `${ index + 1 }/${ formAddresses.length }` }}
                                                    {{ address.name }}
                                                </span>

                                                <span v-if="address.hasError" class="address-status-indicator">
                                                    <i class="icon has-text-danger is-size-3">cancel</i>
                                                </span>
                                            </div>

                                            <div class="chevron" v-if="formAddresses.length > 1">
                                                <i class="icon">expand_less</i>
                                            </div>
                                        </div>
                                    </li>

                                    <transition name="slide">
                                        <li class="fields" v-show="tabStates[index] === true" :class="{ 'mt-3': formAddresses.length < 2 }">
                                            <div class="columns">
                                                <div class="column">
                                                    <div class="field-body">
                                                        <g-input :name="`addresses.${index}.company_name`" :label="$t('digital.order_shipment.company_name')"/>
                                                        <g-input :name="`addresses.${index}.name`" :label="$t('digital.order_shipment.name')"/>
                                                    </div>

                                                    <div class="field-body">
                                                        <g-input :name="`addresses.${index}.email`" :label="$t('digital.order_shipment.email')"/>
                                                    </div>

                                                    <div class="field-body">
                                                        <div class="column p-0">
                                                            <div class="field">
                                                                <g-input :name="`addresses.${index}.street`" :label="$t('digital.order_shipment.street')"/>
                                                            </div>
                                                        </div>

                                                        <div class="column p-0">
                                                            <div class="field is-grouped mb-0">
                                                                <div class="control is-expanded">
                                                                    <g-input :name="`addresses.${index}.house_number`" :label="$t('digital.order_shipment.house_number')"/>
                                                                </div>

                                                                <div class="control is-expanded">
                                                                    <g-input :name="`addresses.${index}.addition`" :label="$t('digital.order_shipment.addition')"/>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="field-body">
                                                        <div class="field-body">
                                                            <g-input :name="`addresses.${index}.zip`" :label="$t('digital.order_shipment.postal_code')"/>
                                                        </div>

                                                        <g-input :name="`addresses.${index}.city`" :label="$t('digital.order_shipment.city')"/>
                                                        <g-select :options="countries" :name="`addresses.${index}.country_id`"
                                                                  :label="$t('digital.order_shipment.country')"/>
                                                    </div>
                                                </div>
                                            </div>

                                        </li>
                                    </transition>
                                </template>
                            </ul>

                        </g-form>
                    </template>

                    <line-table :title="$t('digital.create.order_total')" ref="orderTotal">
                        <template v-slot:body>
                            <tr v-if="form.wrapping !== 'none'">
                                <td class="minimum-width">{{ order.quantity }}x</td>
                                <td>{{ $t('digital.order_shipment.wrapping_short') }} {{ selectedWrapping['name'] }}</td>
                                <td class="minimum-width">{{ (selectedWrapping['price'] * order.quantity) / 100 | currency }}</td>
                            </tr>

                            <tr v-for="(orderCountry, index) in differentOrderCountries" :key="index">
                                <td class="minimum-width">{{ getShipmentQuantityDisplay(orderCountry) }}</td>
                                <td>
                                    {{ $t(`digital.order_shipment.price_${form.shipment_method}`, { country: orderCountry.name, carrier: orderCountry.carrier }) }}
                                </td>
                                <td class="minimum-width align-right">{{ getShipmentPriceDisplay(orderCountry) | currency }}</td>
                            </tr>
                        </template>
                        <template v-slot:footer>
                            <tr>
                                <td colspan="2" class="has-text-right">{{ $t('digital.create.total_excl_vat') }}</td>
                                <td class="minimum-width">
                                    {{ calculatedShipmentPrice }}
                                </td>
                            </tr>
                        </template>
                    </line-table>
                </tabbed-view-item>
            </tabbed-view>
        </template>
    </ModalLayout>
</template>

<script>
import ModalLayout from "../../ModalLayout.vue";
import Accordeon from "../../Accordeon.vue";
import AccordeonItem from "../../AccordeonItem.vue";
import TabbedView from "../../TabbedView.vue";
import TabbedViewItem from "../../TabbedViewItem.vue";
import {mapGetters} from "vuex";
import TagsInput from "@johmun/vue-tags-input";
import LineTable from "../../LineTable.vue";
import GiftcardPreview from "../../../GiftcardPreview.vue";
import debounce from "lodash/debounce";
import posthog from "posthog-js";
import EmailViewItem from "../../Templates/DigitalOrders/EmailViewItem.vue";

export default {
    name: 'DigitalOrderShipment',

    components: {
        EmailViewItem,
        GiftcardPreview,
        LineTable,
        TagsInput,
        TabbedViewItem,
        TabbedView,
        AccordeonItem,
        Accordeon,
        ModalLayout
    },

    props: {
        order: {
            type: Object,
            required: true
        },
        shipmentPrices: {
            type: Object,
            required: true
        }
    },

    computed: {
        ...mapGetters({
            packages: 'packages/all',
            locations: 'locations/allIssuePointSelect',
            reportingTags: 'promotionalOrders/getReportingTags',
            objectPrice: 'promotionalOrders/getPrice',
            countries: 'application/availableCountriesTranslated'
        }),

        successDisabled() {
            // Prevents the user from submitting the form when the shipment method is track-n-trace, but countries
            // that don't support track and trace are selected. In the future, we can expand this function to also
            // disallow submission when other conditions are not met, like not all address fields being filled in.
            return this.shouldShowTrackAndTraceWarning && this.form.shipment_method === 'track-n-trace'
        },

        differentOrderCountries() {
            const countryGroups = {};
            if (this.formAddresses.length <= 0) return [];

            this.formAddresses.forEach(address => {
                const countryId = address.country_id;
                const country = this.countries.find(c => c.value === countryId);

                if (!country) {
                    return;
                }

                const countryCode = country.code;
                const methodPrices = this.shipmentPrices[this.form.shipment_method] || {};
                const countryPrice = methodPrices[countryCode];

                if (!countryPrice) {
                    return;
                }

                if (!countryGroups[countryCode]) {
                    countryGroups[countryCode] = {
                        code: countryCode,
                        name: country.name,
                        quantity: 0,
                        carrier: 'PostNL',
                        price: countryPrice.price,
                        multiply_costs_by_items: countryPrice.multiply_costs_by_items
                    };
                }

                countryGroups[countryCode].quantity++;
            });

            const isBundled = this.form.shipment_bundle === 'bundled';
            const isTrackAndTrace = this.form.shipment_method === 'track-n-trace';

            if (isBundled && Object.keys(countryGroups).length > 0) {
                const countries = Object.values(countryGroups);

                if (isTrackAndTrace) {
                    return countries.map(c => ({ ...c, quantity: 1 }));
                }

                const first = countries[0];
                first.quantity = this.order.quantity;
                return [first];
            }

            return Object.values(countryGroups).sort((a, b) => b.quantity - a.quantity);
        },

        calculatedShipmentPrice() {
            const wrappingPrice = (this.selectedWrapping['price'] * this.order.quantity) / 100;
            let totalShippingPrice = 0;

            const isBundled = this.form.shipment_bundle === 'bundled';
            const isTrackAndTrace = this.form.shipment_method === 'track-n-trace';

            this.differentOrderCountries.forEach(country => {
                const multiply = country.multiply_costs_by_items;

                // If the order is bundled and has track and trace, we only need to add the price once
                if (isBundled && isTrackAndTrace) {
                    totalShippingPrice += country.price / 100;
                }

                // If the order is bundled, does not have track and trace, but multiply costs by items is enabled for the method...
                if (isBundled && !isTrackAndTrace && multiply) {
                    totalShippingPrice += (country.price * this.order.quantity) / 100;
                }

                // If the order is bundled, does not have track and trace, and multiply costs by items is not enabled for the method...
                if (isBundled && !isTrackAndTrace && !multiply) {
                    totalShippingPrice += country.price / 100;
                }

                // If the order has 'unique delivery per gift card' enabled...
                if (!isBundled && multiply) {
                    totalShippingPrice += (country.price * country.quantity) / 100;
                }

                // If the order is not bundled and does not have track and trace, we add the price for each country
                if (!isBundled && !multiply) {
                    totalShippingPrice += (country.price * country.quantity) / 100;
                }
            });

            return this.$options.filters.currency(wrappingPrice + totalShippingPrice);
        },

        // New computed property to check track-n-trace availability
        trackAndTraceUnavailableCountries() {
            if (!this.formAddresses || this.formAddresses.length === 0) {
                return [];
            }

            const unavailableCountries = [];
            const trackAndTracePrices = this.shipmentPrices['track-n-trace'] || {};

            this.formAddresses.forEach(address => {
                const countryId = address.country_id;
                const country = this.countries.find(c => c.value === countryId);

                if (country && !trackAndTracePrices[country.code]) {
                    // Only add unique countries
                    if (!unavailableCountries.find(c => c.code === country.code)) {
                        unavailableCountries.push({
                            code: country.code,
                            name: country.name
                        });
                    }
                }
            });

            return unavailableCountries;
        },

        shouldShowTrackAndTraceWarning() {
            return this.trackAndTraceUnavailableCountries.length > 0;
        },

        availableShipmentMethods() {
            return [
                {
                    id: 'standard',
                    name: this.$t('digital.order_shipment.shipment_standard'),
                },
                {
                    id: 'track-n-trace',
                    name: this.$t('digital.order_shipment.shipment_trackntrace'),
                }
            ];
        },

        selectedWrapping() {
            const wrapping = this.wrappingOptions.find(wrapping => wrapping.id === this.form.wrapping)

            if (wrapping === null || this.form.wrapping === 'none') {
                return {
                    name: this.$t('digital.order_shipment.none'),
                    price: 0
                }
            }

            return wrapping
        },

        formAddresses() {
            let addresses = [];

            for (const key in this.form) {
                if (key.startsWith('addresses.')) {
                    const parts = key.split('.');
                    const index = parseInt(parts[1]);
                    const field = parts[2];
                    const value = this.form[key];

                    if (addresses[index] === undefined) {
                        addresses[index] = {}
                    }

                    addresses[index][field] = value
                }
            }

            if (this.form.shipment_bundle === 'bundled') {
                addresses = addresses.slice(0, 1)
            }

            return addresses
        },

        packageOptions() {
            return this.packages.map((option) => {
                return {
                    name: option.title,
                    value: option.id
                }
            });
        },

        formEndpoint () {
            return `/api/order/${this.order.id}/shipment`
        },
    },

    data() {
        return {
            loading: true,
            activeStep: 1,
            sendTestMailLoading: false,
            activeTab: 0,
            tabItems: [],
            previewCardSide: 'front',
            wrappingOptions: [],
            hasHadElegantSwitchOverToAddresses: false,
            shipmentBundleOptions: [
                {
                    id: 'unique',
                    name: this.$t('digital.order_shipment.bundle_unique'),
                },
                {
                    id: 'bundled',
                    name: this.$t('digital.order_shipment.bundle_bundle'),
                }
            ],
            previewData: {
                title: null,
                value: 4500,
                message: '',
            },
            fieldStepMap: {
                1: [
                    'order_reference',
                    'sender_name',
                    'sender_email',
                    'subject',
                    'message',
                ],
                2: [
                    'addresses'
                ],
            },
            tabStates: {},
            form: null
        }
    },
    watch: {
        activeTab: function () {
            this.setScrollableElementHeight()
        },

        'activeStep': function () {
            // When the user switches to the shipment step, we elegantly switch over to the addresses tab (the first time)
            // TODO: Fix marker bug not sliding over to the 2nd tab
            if (this.hasHadElegantSwitchOverToAddresses === false) {
                this.hasHadElegantSwitchOverToAddresses = true;
                this.activeTab = 2;
            }
        },

        form: {
            handler: debounce(function () {
                this.previewData.value = 4500
                this.previewData.message = this.form.message

                if (this.form.shipment_bundle === 'bundled') {
                    Object.keys(this.tabStates).forEach(key => {
                        this.tabStates[key] = false
                    })

                    this.tabStates[0] = true
                }


                if (this.form.value_type === 'amount') {
                    this.previewData.title = undefined
                } else {
                    const gcPackage = this.packageOptions.find(option => option.value === this.form.package_id)
                    this.previewData.title = this.form.package_id ? gcPackage.name : null
                }
            }, 100),
            deep: true,
        },
    },

    async mounted() {
        await this.$store.dispatch('packages/loadAll')
        await this.$store.dispatch('locations/loadAll')
        await this.$store.dispatch('application/loadAllCountries')

        this.loading = false
    },

    created() {
        setInterval(() => {
            this.setScrollableElementHeight()
        }, 100)

        let formFields = {
            wrapping: 'none',
            shipment_method: 'track-n-trace',
            shipment_bundle: 'bundled',
            accept_conditions: false,
            order_reference: this.$t('digital.quantity_worth', {
                quantity: this.$tc('digital.gift_card_plural', this.order.quantity, { amount: this.order.quantity }),
                price: this.$options.filters.currency(this.order.amount / 100)
            })
        }

        // Remove the first option ('unique') from the options, if there's only one gift card
        if (this.order.quantity < 2) {
            this.shipmentBundleOptions.shift()
            formFields.shipment_bundle = this.shipmentBundleOptions[0].id
        }

        // Initialize Form with the base values first
        this.form = new Form(formFields)

        // Then add address fields dynamically using dot notation
        for (let i = 0; i < this.order.quantity; i++) {
            this.$set(this.tabStates, i, i === 0)

            this.form.addField(`addresses.${i}.tabActive`, false)
            this.form.addField(`addresses.${i}.hasError`, false)
            this.form.addField(`addresses.${i}.name`, '')
            this.form.addField(`addresses.${i}.email`, '')
            this.form.addField(`addresses.${i}.company_name`, '')
            this.form.addField(`addresses.${i}.street`, '')
            this.form.addField(`addresses.${i}.house_number`, '')
            this.form.addField(`addresses.${i}.addition`, '')
            this.form.addField(`addresses.${i}.zip`, '')
            this.form.addField(`addresses.${i}.city`, '')
            this.form.addField(`addresses.${i}.country_id`, 1)
        }
    },

    beforeCreate() {
        axios.get(`/api/custom-wrappings`)
             .then(response => {
                 if (this.form.wrapping === '') {
                    this.form.wrapping = response.data.data.wrappings[0].id ?? ''
                 }

                 this.wrappingOptions = response.data.data.wrappings.map(wrapping => {
                     return {
                         id: wrapping.id,
                         name: wrapping.name,
                         image: typeof wrapping.image !== 'undefined' ? {
                             src: wrapping.image,
                             width: '2.5rem',
                             height: '1.75rem',
                         } : null,
                         price: wrapping.company_price
                     }
                 })

                 this.wrappingOptions.unshift({
                     id: 'none',
                     name: this.$t('digital.order_shipment.no_wrapping'),
                     image: {
                         src: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
                         width: '0rem',
                         height: '1.75rem',
                     },
                     price: 0,
                 })
             })
    },

    methods: {
        setScrollableElementHeight() {
            if (typeof this.$refs.formRef === 'undefined' || typeof this.$refs.orderTotal === 'undefined') {
                return
            }

            const tableHeight = this.$refs.formRef.$el.clientHeight
            const orderTotalHeight = this.$refs.orderTotal.$data.active ? this.$refs.orderTotal.$el.clientHeight
                                                                        : 45

            // Dynamically set the scroll height of the addresses list
            this.$refs.addresses.$el.style.height = `${tableHeight - orderTotalHeight}px`
            this.$refs.addresses.$el.style.overflowY = 'scroll'
        },

        getShipmentQuantityDisplay(orderCountry) {
            const isTrackAndTrace = this.form.shipment_method === 'track-n-trace';
            const isBundled = this.form.shipment_bundle === 'bundled';

            // If the order is bundled and has track and trace, we only need to show 1x
            if (isTrackAndTrace && isBundled) {
                return '1x';
            }

            // If the order is track and trace, but not bundled, we show the quantity of the order country
            if (isTrackAndTrace && !isBundled) {
                return `${orderCountry.quantity}x`;
            }

            // If the order is not track and trace, but bundled (so one country), we show the quantity of the order
            if (!isTrackAndTrace && isBundled) {
                return `${this.order.quantity}x`;
            }

            // If the order is not track and trace and not bundled, we show the quantity of the order country
            if (!isBundled) {
                return `${orderCountry.quantity}x`;
            }

            return '';
        },

        /**
         * This function can be a bit complex, so all conditions are handled separately, to make it easier to
         * understand the logic.
         */
        getShipmentPriceDisplay(orderCountry) {
            const isBundledShipment = this.form.shipment_bundle === 'bundled';
            const isTrackAndTrace = this.form.shipment_method === 'track-n-trace';
            const multiply = orderCountry.multiply_costs_by_items;

            // If the order is bundled and has track and trace...
            if (isBundledShipment && isTrackAndTrace) {
                return orderCountry.price / 100;
            }

            // If the order is bundled, does not have track and trace, but multiply costs by items is enabled for the method...
            if (isBundledShipment && !isTrackAndTrace && multiply) {
                return (orderCountry.price * this.order.quantity) / 100;
            }

            // If the order is bundled, does not have track and trace, and multiply costs by items is not enabled for the method...
            if (isBundledShipment && !isTrackAndTrace && !multiply) {
                return orderCountry.price / 100;
            }

            // If the order has 'unique delivery per gift card' enabled...
            if (!isBundledShipment) {
                return (orderCountry.price * orderCountry.quantity) / 100;
            }

            return 0;
        },

        before(form) {
            this.setLoadingState(true)

            // Remove empty string values
            Object.keys(form).forEach(key => {
                if (form[key] === '') {
                    delete form[key]
                }
            });

            return form
        },

        toggleAddressPopout(index) {
            Object.keys(this.tabStates).forEach(key => {
                this.tabStates[key] = false
            });

            this.tabStates[index] = true
        },

        close() {
            this.$emit('destroy');
        },

        selectedStep(step) {
            if(this.activeStep === step) {
                step = step + 1;
            }

            if(step >= 3) {
                step = 1;
            }

            this.activeStep = step;
        },

        setLoadingState(state) {
            this.loading = state;
        },

        setActiveStepForErrors(data) {
            // If data object does not contain an errors object, return
            if (!data.errors) {
                return;
            }

            let formErrorFields = [];

            // Loop through the errors object, it has the format of {field: [errors]}
            for (const field in data.errors) {
                // If the field is not in the form object, continue
                if (!this.form.hasOwnProperty(field)) {
                    continue;
                }

                // Add the field to the formErrorFields array
                formErrorFields.push(field);
            }

            let stepsWithErrors = [];

            // Loop through the fieldStepMap object
            for (const step in this.fieldStepMap) {
                // Loop through the fields in the fieldStepMap object
                for (const field of this.fieldStepMap[step]) {
                    // If the field is in the formErrorFields array, add the step to the stepsWithErrors array
                    if (formErrorFields.includes(field)) {
                        stepsWithErrors.push(parseInt(step));
                        break;
                    }
                }
            }

            // If the stepsWithErrors array is not empty, set the activeStep to the lowest step
            if (stepsWithErrors.length > 0) {
                this.activeStep = Math.min(...stepsWithErrors);
            }

            // if there's error fields that start with "addresses", grab the index behind it and set "hasError" to true
            for (const field in data.errors) {
                if (field.startsWith('addresses.')) {
                    const parts = field.split('.');
                    const index = parseInt(parts[1]);
                    if (this.formAddresses[index]) {
                        // Use Vue.set to ensure reactivity
                        this.$set(this.formAddresses[index], 'hasError', true);
                    }
                }
            }
        },

        submitForm() {
            this.$refs.formRef.submit()
        },

        formSuccess(response) {
            // Keep loading state while we're loading the orders
            this.loading = true

            this.$buefy.toast.open({
                message: this.$t('digital.order_shipment.success'),
                type: 'is-success',
                duration: 5000,
            })

            this.$store.dispatch('promotionalOrders/loadAll')
                .then(() => {
                    posthog.capture(
                        'create_digital_gift_cards_shipment',
                        {
                            $set_once: { has_created_digital_gift_cards_shipment: true },
                        }
                    )

                    this.close()
                })
        },
    }
}
</script>

<style scoped lang="scss">
.download-template {
    color: #313131;
    text-decoration: underline;
    padding-left: 0.6rem;
    font-size: 0.875rem;
}

.form-min-height {
    min-height: 100%;
}

.form-container {
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
}

p.email-body {
    white-space: pre-wrap;
    overflow-wrap: break-word;
}

.header ul li {
    padding: 0.5rem 1rem;
}

ul.addresses {
    list-style: none;
    margin: 0;

    li {
        font-size: 0.938rem;
        color: #6A7272;
        padding: .38rem 1rem;
        margin: 0;
        background-color: #f7f9fb;
        border-bottom: 0.063rem solid #efefef;
    }

    .fields {
        padding-top: 0.6875rem;
        background-color: rgba(0, 0, 0, 0);
    }

    .address-header {
        cursor: pointer;
        align-items: center;
        display: flex;

        .name {
            flex: 1;

            .address-status-indicator {
                position: relative;
                top: 0.188rem;
            }

            .step {
                margin-right: 0.313rem;
            }
        }

        .chevron {
            flex: 1;
            text-align: right;

            i.icon {
                color: #6A7272;
                transform: rotate(90deg);
                transition: color .2s, transform .2s ease-in-out;
            }
        }

        &.active {
            font-weight: bold;

            .chevron i.icon {
                color: #1f1f1f;
                vertical-align: middle;
                transform: rotate(180deg);
            }
        }
    }
}

input.disabled {
    opacity: 0.5;
}

.preview-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 1rem 0;
    max-width: 65%;
    align-self: center;

    @media (min-height: 850px) {
        max-width: 75%;
        margin-bottom: 2rem;
    }

    .controls {
        display: flex;
        justify-content: space-between;

        a {
            color: #1F1F1F;
            text-decoration: underline;

            &.is-active {
                cursor: default;
                font-weight: bold;
                text-decoration: none;
            }
        }
    }

    ::v-deep {

        canvas {
            max-width: 100% !important;
            height: auto !important;
            box-shadow: 0 11px 54px 0 rgba(0, 0, 0, 0.10);
            border-radius: 3px;
            background: #FFFFFF;
        }
    }
}

td.align-right {
    text-align: right;
}

.tnt-notification {
    display: inline-flex;
    align-items: center;
    max-width: 32rem;
    white-space: normal;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;

    i.icon {
        font-size: 20px;
        position: relative;
        top: 3px;
    }
}
</style>
